import { Check, Close } from '@mui/icons-material';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Button, Switch } from '@sb/cc-design-system';
import { type ChangeEvent, useState } from 'react';

export const DesignSystemPage = () => {
  const [clickCount, setClickCount] = useState(0);
  const [isDangerMode, setIsDangerMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [iconSwitchEnabled, setIconSwitchEnabled] = useState(true);
  const [compactSwitchEnabled, setCompactSwitchEnabled] = useState(false);
  const [iosSwitchEnabled, setIosSwitchEnabled] = useState(true);

  const handleSwitchChange = (
    setValue: (checked: boolean) => void,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setValue(event.target.checked);
  };

  const handleSaveClick = () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    window.setTimeout(() => {
      setSaveCount((value) => value + 1);
      setIsSaving(false);
    }, 900);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Design System Playground
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Interactive checks for DS Button and Switch components.
          </Typography>
        </Box>

        <Divider />

        <Stack spacing={1.5}>
          <Typography variant="h6">Button</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained-primary"
              size="large"
              onClick={() => setClickCount((value) => value + 1)}>
              Click me ({clickCount})
            </Button>
            <Button
              variant={isDangerMode ? 'contained-error' : 'contained-secondary'}
              size="large"
              onClick={() => setIsDangerMode((value) => !value)}>
              {isDangerMode ? 'Error mode on' : 'Error mode off'}
            </Button>
            <Button
              variant="outlined-secondary-1"
              size="large"
              loading={isSaving}
              onClick={handleSaveClick}
              startIcon={<Check />}>
              {isSaving ? 'Saving...' : `Save (${saveCount})`}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Every button above updates state when clicked.
          </Typography>
        </Stack>

        <Divider />

        <Stack spacing={1.5}>
          <Typography variant="h6">Switch</Typography>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Switch
                checked={marketingEnabled}
                onChange={(event) =>
                  handleSwitchChange(setMarketingEnabled, event)
                }
              />
              <Typography variant="body2">
                Marketing notifications: {marketingEnabled ? 'On' : 'Off'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Switch
                checked={iconSwitchEnabled}
                onIcon={<Check fontSize="inherit" />}
                offIcon={<Close fontSize="inherit" />}
                onChange={(event) =>
                  handleSwitchChange(setIconSwitchEnabled, event)
                }
              />
              <Typography variant="body2">
                Icon switch: {iconSwitchEnabled ? 'Enabled' : 'Disabled'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Switch
                checked={compactSwitchEnabled}
                width={44}
                height={24}
                onIcon={null}
                onChange={(event) =>
                  handleSwitchChange(setCompactSwitchEnabled, event)
                }
              />
              <Typography variant="body2">
                Compact switch: {compactSwitchEnabled ? 'On' : 'Off'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Switch
                checked={iosSwitchEnabled}
                width={52}
                height={30}
                onIcon={null}
                offIcon={null}
                backgroundColor="#34C759"
                onChange={(event) =>
                  handleSwitchChange(setIosSwitchEnabled, event)
                }
              />
              <Typography variant="body2">
                iOS green track: {iosSwitchEnabled ? 'On' : 'Off'}
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Every switch above is controlled and fully clickable.
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default DesignSystemPage;
